package com.zi.support.shared

/** One company backend the API Health map can point a "Try it out" test at, per environment. */
data class ApiServer(val id: String, val label: String, val productionBaseUrl: String, val testBaseUrl: String?)

/** A single real endpoint on the shared GT Dataserver API. `{name}` segments in [path] are filled in before sending. */
data class ApiEndpointDef(val method: String, val path: String, val label: String)

data class ApiAppDef(val name: String, val endpoints: List<ApiEndpointDef>)

/**
 * Real servers and endpoints pulled from this company's other app codebases (BoilerTracker,
 * CentralDriver, AttendanceTracking, "Operations App"/openapi.json) rather than invented paths,
 * so the health check reports on routes that actually exist. Production URLs match the company
 * directory already shown under More > Servers; test URLs point at each company's private
 * Tailscale dev box where one is known (currently only Gantrans).
 * Shared by web, Android and iOS (More > API).
 */
object ApiCatalog {
    val servers = listOf(
        ApiServer("gantrans", "Gantrans", "https://gantrans.net.za:56308", "https://vmi3493472.tailc4ba6c.ts.net:56310"),
        ApiServer("kph", "KPH", "https://kphlogistics.net.za:56308", null),
        ApiServer("transport", "Transport", "https://transportdbn.net.za:56308", null),
        ApiServer("delta", "Delta", "https://deltaconsol.net.za:56308", null),
        ApiServer("texas", "Texas", "https://texastrucking.net.za:56308", null),
    )

    // From BoilerTracker/app/.../ApiConfig.kt + BackendApiClient.kt (every real call, not a curated subset).
    private val heatingEndpoints = listOf(
        ApiEndpointDef("GET", "/haulier_fleet?fleettypeid=34", "Heating stations (fleet)"),
        ApiEndpointDef("GET", "/api/heating/bookings", "Heating bookings"),
        ApiEndpointDef("POST", "/api/heating/booking", "Create heating booking"),
        ApiEndpointDef("POST", "/api/heatingbooking/allocate", "Allocate booking"),
        ApiEndpointDef("POST", "/api/heatingbooking/complete", "Complete booking"),
        ApiEndpointDef("POST", "/api/heatingbooking/hotswap", "Hot-swap booking"),
        ApiEndpointDef("POST", "/api/heatingbooking/reassign", "Reassign booking"),
        ApiEndpointDef("POST", "/api/heatingbooking/disconnect", "Disconnect booking"),
        ApiEndpointDef("GET", "/bookingitem", "Booking items"),
        ApiEndpointDef("GET", "/bookingitemevent", "Booking item events"),
        ApiEndpointDef("GET", "/bookingitem_journal", "Booking item journal"),
        ApiEndpointDef("POST", "/bookingitem_journal", "Add booking item journal entry"),
        ApiEndpointDef("GET", "/api/heating/temperature-readings", "Temperature readings"),
        ApiEndpointDef("GET", "/api/heating/boiler/reading", "Boiler readings"),
        ApiEndpointDef("GET", "/depot_gate_stock", "Depot gate stock"),
        ApiEndpointDef("GET", "/api/bulk_products?limit=5000", "Bulk products"),
        ApiEndpointDef("GET", "/customers?limit=5000", "Customers"),
        ApiEndpointDef("GET", "/accesscontrol_useraccounts", "User accounts"),
        ApiEndpointDef("GET", "/systemdefaults?limit=1000", "System defaults"),
    )

    // From CentralDriver/composeApp/.../js/api-client.js — the entire real endpoint set; this
    // "Driver Hub" app is rebranded per tenant but the API surface is identical.
    private val driverEndpoints = listOf(
        ApiEndpointDef("GET", "/api/driverapp/bookings", "Assigned bookings"),
        ApiEndpointDef("POST", "/api/haulier_drivers/verify", "Driver login / verify"),
        ApiEndpointDef("POST", "/api/complete_booking", "Complete booking"),
    )

    // AttendanceTracking (package com.zi.DepoFlowA) talks to Firestore directly and has no direct
    // GT Dataserver calls of its own, so its real API surface is the attendance procedure group
    // from "Operations App"/openapi.json ("Live GT Dataserver") — the only endpoints that exist
    // for this domain.
    private val attendanceEndpoints = listOf(
        ApiEndpointDef("POST", "/api/proc/attendance/assign-payperiod", "Execute usp_Attendance_AssignToPayPeriod"),
        ApiEndpointDef("POST", "/api/proc/attendance/delete", "Execute usp_Attendance_DeleteAttendance"),
        ApiEndpointDef("POST", "/api/proc/attendance/drivers", "Execute usp_Attendance_GetDrivers"),
        ApiEndpointDef("POST", "/api/proc/attendance/insert", "Execute usp_Attendance_InsertAttendance"),
        ApiEndpointDef("POST", "/api/proc/attendance/timeline", "Execute usp_Attendance_GetTimeline"),
        ApiEndpointDef("POST", "/api/proc/attendance/types", "Execute usp_Attendance_GetAttendanceTypes"),
        ApiEndpointDef("POST", "/api/proc/attendance/update", "Execute usp_Attendance_UpdateAttendance"),
    )

    // From "Operations App"/composeApp/.../*Api.kt + Network.kt: only the calls the app actually makes
    // (inspections, trip checks, coil bookings, purchase orders, depot authorisation), not the whole spec.
    private val operationsEndpoints = listOf(
        ApiEndpointDef("POST", "/api/dashboardapp_login", "Staff login"),
        ApiEndpointDef("GET", "/api/haulier_drivers", "Drivers"),
        ApiEndpointDef("GET", "/haulier_fleet", "Fleet (trucks)"),
        ApiEndpointDef("GET", "/haulier_trailers", "Trailers"),
        ApiEndpointDef("GET", "/api/haulier_fleet_divisions", "Fleet divisions"),
        ApiEndpointDef("GET", "/systemdefaults", "System defaults"),
        ApiEndpointDef("GET", "/api/ws/tripchecks", "Trip checks"),
        ApiEndpointDef("POST", "/api/ws/tripchecks", "Create trip check"),
        ApiEndpointDef("GET", "/api/ws/tripchecks/{tripCheckId}", "Trip check by ID"),
        ApiEndpointDef("PUT", "/api/ws/tripchecks/{tripCheckId}", "Update / complete trip check"),
        ApiEndpointDef("GET", "/api/ws/tripcheck/trailers", "Trip check trailers"),
        ApiEndpointDef("POST", "/api/ws/tripcheck/trailers", "Add trip check trailer"),
        ApiEndpointDef("GET", "/api/ws/tripcheck/issues", "Trip check issues"),
        ApiEndpointDef("POST", "/api/ws/tripcheck/issues", "Add trip check issue note"),
        ApiEndpointDef("GET", "/api/ws/tripcheck/images", "Trip check images"),
        ApiEndpointDef("POST", "/api/ws/tripcheck/images", "Add trip check image"),
        ApiEndpointDef("GET", "/api/ws/tripcheck/damage", "Trip check damage"),
        ApiEndpointDef("POST", "/api/ws/tripcheck/damage", "Add trip check damage"),
        ApiEndpointDef("GET", "/api/ws/tripcheck/template/category", "Checklist template categories"),
        ApiEndpointDef("GET", "/api/ws/tripcheck/template/item", "Checklist template items"),
        ApiEndpointDef("GET", "/api/ws/componenttype", "Component types"),
        ApiEndpointDef("GET", "/api/ws/damagetype", "Damage types"),
        ApiEndpointDef("GET", "/api/ws/severity", "Damage severities"),
        ApiEndpointDef("GET", "/bookingtype?limit=200", "Booking types"),
        ApiEndpointDef("GET", "/bookingtypes-subitems?limit=500", "Booking subtypes"),
        ApiEndpointDef("POST", "/api/depot/bookings", "Depot bookings (search)"),
        ApiEndpointDef("GET", "/bookingitem", "Booking items"),
        ApiEndpointDef("GET", "/bookingitem/{BookingItemID}", "Booking item by ID"),
        ApiEndpointDef("PUT", "/bookingitem/{BookingItemID}", "Authorise / update booking item"),
        ApiEndpointDef("POST", "/bookingitem_journal", "Add booking item journal entry"),
        ApiEndpointDef("GET", "/bookingitemevent?BookingItemID={BookingItemID}&limit=100", "Booking item events"),
        ApiEndpointDef("POST", "/bookingitemevent", "Record coil booking event"),
        ApiEndpointDef("GET", "/serviceproviderevent?limit=200", "Service provider events"),
        ApiEndpointDef("GET", "/ibtinformation/{BookingItemID}", "IBT cargo information"),
        ApiEndpointDef("GET", "/api/search", "Booking search"),
        ApiEndpointDef("GET", "/api/search2025", "Booking search (2025)"),
        ApiEndpointDef("GET", "/depot_gate_stock?Active=true&limit=5000", "Depot gate stock"),
        ApiEndpointDef("GET", "/purchaseorder_headers", "Purchase orders"),
        ApiEndpointDef("GET", "/purchaseorder_header/{PurchaseOrderID}", "Purchase order by ID"),
        ApiEndpointDef("PUT", "/purchaseorder_header/{PurchaseOrderID}", "Update purchase order"),
        ApiEndpointDef("GET", "/purchaseorder_details", "Purchase order lines"),
        ApiEndpointDef("GET", "/get_wms_purchaseorder_journal", "Purchase order journal"),
        ApiEndpointDef("GET", "/serviceproviders", "Suppliers"),
        ApiEndpointDef("GET", "/fleet_expense", "Fleet expenses"),
        ApiEndpointDef("GET", "/customer_contacts", "Customer contacts"),
    )

    // From ~/Downloads/ImageRecognitionSoftware (package com.zi.depoflow): every call its gate screens and
    // GateSyncWorker make. Gate-in/out and attachments are POSTs (the Dataserver spec agrees).
    private val depoFlowEndpoints = listOf(
        ApiEndpointDef("POST", "/api/dashboardapp_login", "Gate staff login"),
        ApiEndpointDef("GET", "/depot_gate_stock?include_booking=false&page=1&page_size=100", "Today's gate entries"),
        ApiEndpointDef("POST", "/depot_gate_stock/gatein", "Gate in"),
        ApiEndpointDef("POST", "/depot_gate_stock/gateout", "Gate out"),
        ApiEndpointDef("POST", "/depot_gate_stock_attachment", "Upload container photo"),
        ApiEndpointDef("POST", "/depot_gate_zeropayload", "Zero-payload (empty truck) movement"),
        ApiEndpointDef("GET", "/check-ins/enrichment", "Quick gate-in enrichment"),
        ApiEndpointDef("GET", "/customers?ChargeStorageMT=1", "Customers (storage)"),
        ApiEndpointDef("GET", "/api/haulier_drivers?page=1&page_size=500", "Drivers"),
        ApiEndpointDef("GET", "/haulier_fleet", "Fleet (trucks)"),
        ApiEndpointDef("GET", "/haulier_trailers/active", "Active trailers"),
        ApiEndpointDef("GET", "/static_container_iso_types", "Container ISO types"),
    )

    // From CustomerApp (CargoView) CustomerRepository.kt. Its live vessel map uses a separate tracking
    // server, not the Dataserver, so it isn't listed here.
    private val cargoViewEndpoints = listOf(
        ApiEndpointDef("POST", "/api/customerportal_login", "Customer login"),
        ApiEndpointDef("POST", "/api/search2025", "Search bookings"),
        ApiEndpointDef("POST", "/api/bookingitem_tpt/batch", "Booking transport details (batch)"),
        ApiEndpointDef("POST", "/api/bookingitemevents/vehicle-positions", "Vehicle positions"),
        ApiEndpointDef("GET", "/bookingitemevent?BookingItemID={BookingItemID}&limit=100&offset=0", "Booking item events"),
        ApiEndpointDef("GET", "/serviceproviderevent?limit=500&offset=0", "Service provider events"),
    )

    val apps = listOf(
        ApiAppDef("Heating App", heatingEndpoints),
        ApiAppDef("Driver App", driverEndpoints),
        ApiAppDef("Operations App", operationsEndpoints),
        ApiAppDef("DepoFlow", depoFlowEndpoints),
        ApiAppDef("DepoFlowA", attendanceEndpoints),
        ApiAppDef("CargoView", cargoViewEndpoints),
    )

    /** Write calls (anything but GET) only ever go to Test servers; the checkApiEndpoint function enforces it too. */
    fun isAllowed(method: String, environment: String): Boolean = method.uppercase() == "GET" || environment == "test"

    /** `{BookingItemID}`-style placeholders in a path, in order, without braces. */
    fun pathParams(path: String): List<String> = Regex("\\{([^}]+)\\}").findAll(path).map { it.groupValues[1] }.toList()

    /** Fills placeholders (URL-encoding each value) and appends [query] ("a=1&b=2", "?" optional). */
    fun buildPath(path: String, values: Map<String, String>, query: String = ""): String {
        var filled = path
        for (name in pathParams(path)) filled = filled.replace("{$name}", encode(values[name].orEmpty().trim()))
        val extra = query.trim().removePrefix("?")
        if (extra.isEmpty()) return filled
        return filled + (if ('?' in filled) "&" else "?") + extra
    }

    private fun encode(value: String): String = buildString {
        for (byte in value.encodeToByteArray()) {
            val c = byte.toInt().toChar()
            if (c.isLetterOrDigit() && byte >= 0 || c in "-_.~") append(c) else append("%" + (byte.toInt() and 0xFF).toString(16).uppercase().padStart(2, '0'))
        }
    }
}
